import { useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

/**
 * Campo de contraseña con toggle de visibilidad (ojo).
 */
function PasswordInput({ testId, value, onChange, placeholder, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        data-testid={testId}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        data-testid={`${testId}-toggle`}
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        title={visible ? "Ocultar" : "Mostrar"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-green-700 p-1 rounded"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

/**
 * Modal global para cambio de contraseña.
 * - Si `forced` (must_change_password) está activo: no se puede cerrar hasta cambiarla.
 * - Si `forced` es false: se puede cerrar libremente (uso voluntario desde un botón).
 */
export default function ChangePasswordDialog({ open, forced = false, onClose, onSuccess }) {
  const { token, API, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    // Trim para evitar espacios invisibles introducidos por autofill / pegado
    const curT = (current || "").trim();
    const nxtT = (next || "").trim();
    const cfmT = (confirm || "").trim();
    if (!curT || !nxtT || !cfmT) { toast.error("Complete todos los campos"); return; }
    if (nxtT !== cfmT) { toast.error("Las contraseñas nuevas no coinciden"); return; }
    if (nxtT.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    try {
      setLoading(true);
      await axios.post(`${API}/auth/change-password`, { current_password: curT, new_password: nxtT }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrent(""); setNext(""); setConfirm("");
      if (forced) {
        // En cambio forzado (primer login con clave temporal): cerrar sesión y forzar re-login
        // con la NUEVA clave. Así el usuario verifica inmediatamente que su clave nueva funciona
        // y evitamos que se dé cuenta del problema días después.
        toast.success("Contraseña actualizada. Inicie sesión con su nueva contraseña.");
        logout();
      } else {
        toast.success("Contraseña actualizada");
        onSuccess && onSuccess();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar contraseña");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !forced) onClose && onClose(); }}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => forced && e.preventDefault()} onEscapeKeyDown={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-900"><KeyRound className="w-5 h-5 text-orange-500" /> {forced ? "Establezca su nueva contraseña" : "Cambiar contraseña"}</DialogTitle>
        </DialogHeader>
        {forced && (
          <div className="text-sm bg-orange-50 border border-orange-100 text-orange-800 rounded-md p-3" data-testid="forced-banner">
            Por seguridad, debe cambiar su contraseña temporal antes de continuar usando el portal.
          </div>
        )}
        <div className="space-y-3">
          <div><Label>Contraseña actual</Label><PasswordInput testId="cp-current" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} placeholder="Su contraseña actual" /></div>
          <div><Label>Nueva contraseña</Label><PasswordInput testId="cp-new" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} placeholder="Mínimo 6 caracteres" /></div>
          <div><Label>Confirmar nueva contraseña</Label><PasswordInput testId="cp-confirm" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repita la nueva contraseña" /></div>
        </div>
        <DialogFooter className="gap-2">
          {!forced && <Button variant="outline" onClick={() => onClose && onClose()} disabled={loading}>Cancelar</Button>}
          <Button data-testid="cp-submit" onClick={submit} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white">{loading ? "Guardando..." : "Cambiar contraseña"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
